import { useEffect, useState } from 'react';

/**
 * Spotify-style dominant color extraction from an image URL.
 * Returns an "r,g,b" string (for easy rgba() composition) or the fallback.
 * Same-origin thumbnails work with auth cookies; cross-origin covers use
 * anonymous CORS and silently fall back on taint/failure.
 */
export const FALLBACK_RGB = '34,197,94';

export function useDominantColor(src, fallback = FALLBACK_RGB) {
  const [rgb, setRgb] = useState(fallback);

  useEffect(() => {
    if (!src) {
      setRgb(fallback);
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const size = 24;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        let r = 0;
        let g = 0;
        let b = 0;
        let n = 0;
        for (let i = 0; i < data.length; i += 4) {
          const rr = data[i];
          const gg = data[i + 1];
          const bb = data[i + 2];
          const max = Math.max(rr, gg, bb);
          const min = Math.min(rr, gg, bb);
          const lum = (rr + gg + bb) / 3;
          // weight saturated, mid-luminance pixels (like Spotify's vibrant pick)
          const w = 1 + (max - min) / 48 + (lum > 40 && lum < 215 ? 1.5 : 0);
          r += rr * w;
          g += gg * w;
          b += bb * w;
          n += w;
        }
        if (!cancelled && n > 0) {
          setRgb(`${Math.round(r / n)},${Math.round(g / n)},${Math.round(b / n)}`);
        }
      } catch (err) {
        // tainted canvas (cross-origin without CORS) - keep fallback glow color
        console.debug('Dominant color extraction skipped:', err?.message);
      }
    };
    img.onerror = () => {};
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src, fallback]);

  return rgb;
}
